import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { useLazyGetCashReportCyclePdfQuery } from '../../Service/extendedCashDocumentsApi';
import { toCashReportsUserMessage } from '../../Utils/cashReportsErrorMessage';

interface CashReportPdfActionsProps {
  cycleId: number;
  cycleMonth: string;
}

const PRINT_URL_FALLBACK_REVOKE_DELAY_MS = 60_000;
const PRINT_WINDOW_CLOSE_POLL_INTERVAL_MS = 500;

const CashReportPdfActions: React.FC<CashReportPdfActionsProps> = ({ cycleId, cycleMonth }) => {
  const [triggerGetCashReportCyclePdf, { isFetching }] = useLazyGetCashReportCyclePdfQuery();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const fetchPdfBlob = async (): Promise<Blob> => {
    const pdfBlob = await triggerGetCashReportCyclePdf(cycleId).unwrap();
    if (!(pdfBlob instanceof Blob) || pdfBlob.size === 0) {
      throw new Error('Pobrany PDF raportu kasowego jest pusty.');
    }

    return pdfBlob;
  };

  const handlePreview = async () => {
    setActionError(null);

    try {
      const pdfBlob = await fetchPdfBlob();
      const nextPreviewUrl = window.URL.createObjectURL(pdfBlob);

      setPreviewUrl((previousPreviewUrl) => {
        if (previousPreviewUrl) {
          window.URL.revokeObjectURL(previousPreviewUrl);
        }

        return nextPreviewUrl;
      });
    } catch (error) {
      setActionError(toCashReportsUserMessage(error, 'Nie udało się pobrać podglądu PDF raportu kasowego.'));
    }
  };

  const handleDownload = async () => {
    setActionError(null);

    try {
      const pdfBlob = await fetchPdfBlob();
      const downloadUrl = window.URL.createObjectURL(pdfBlob);

      try {
        const link = window.document.createElement('a');
        link.href = downloadUrl;
        link.download = `cash-report-cycle-${cycleMonth}.pdf`;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      } finally {
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      setActionError(toCashReportsUserMessage(error, 'Nie udało się pobrać PDF raportu kasowego.'));
    }
  };

  const handlePrint = async () => {
    setActionError(null);
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      setActionError(
        'Przeglądarka zablokowała okno wydruku PDF. Zezwól na wyskakujące okna i spróbuj ponownie.'
      );
      return;
    }

    try {
      const pdfBlob = await fetchPdfBlob();
      const printUrl = window.URL.createObjectURL(pdfBlob);
      let wasRevoked = false;
      let revokeFallbackTimeout: number | null = null;
      let closePollInterval: number | null = null;
      const revokePrintUrl = () => {
        if (wasRevoked) {
          return;
        }

        wasRevoked = true;
        if (revokeFallbackTimeout !== null) {
          window.clearTimeout(revokeFallbackTimeout);
        }
        if (closePollInterval !== null) {
          window.clearInterval(closePollInterval);
        }
        window.URL.revokeObjectURL(printUrl);
      };
      revokeFallbackTimeout = window.setTimeout(revokePrintUrl, PRINT_URL_FALLBACK_REVOKE_DELAY_MS);
      closePollInterval = window.setInterval(() => {
        if (printWindow.closed) {
          revokePrintUrl();
        }
      }, PRINT_WINDOW_CLOSE_POLL_INTERVAL_MS);

      if (printWindow.closed) {
        revokePrintUrl();
        return;
      }

      const printPdfFromTab = () => {
        if (printWindow.closed) {
          revokePrintUrl();
          return;
        }

        printWindow.focus();
        printWindow.print();
      };

      if (typeof printWindow.addEventListener === 'function') {
        printWindow.addEventListener('load', printPdfFromTab, { once: true });
        printWindow.addEventListener('afterprint', revokePrintUrl, { once: true });
      } else {
        printPdfFromTab();
        revokePrintUrl();
      }

      printWindow.location.href = printUrl;
    } catch (error) {
      if (!printWindow.closed) {
        printWindow.close();
      }
      setActionError(toCashReportsUserMessage(error, 'Nie udało się wydrukować PDF raportu kasowego.'));
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          onClick={handlePreview}
          disabled={isFetching}
        >
          Podgląd PDF
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={isFetching}
        >
          Drukuj PDF
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={isFetching}
        >
          Pobierz PDF
        </Button>
      </Stack>

      {actionError && <Alert severity="error">{actionError}</Alert>}

      {previewUrl && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Podgląd raportu kasowego {cycleMonth}
            </Typography>
            <IconButton
              size="small"
              aria-label="Zamknij podgląd PDF"
              onClick={() => setPreviewUrl(null)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="iframe"
            title={`Podgląd raportu kasowego ${cycleMonth}`}
            src={previewUrl}
            sx={{ width: '100%', height: 560, border: 0, borderRadius: 1 }}
          />
        </Box>
      )}
    </Stack>
  );
};

export default CashReportPdfActions;
